import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import EditIcon from '@mui/icons-material/Edit';
import { postApiCall } from 'api/apiService';
import { API_URL, UPLOAD_BUYER_SIGN } from 'constant/app.config';
import { poppins400 } from 'theme/common.style';
import Loading from 'assets/images/loading.png';

const CustomSignaturePadDialog = ({
  open,
  onClose,
  onSave,
  userDetail,
  onApplied,
}) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const theme = useTheme();

  const [mode, setMode] = useState('DRAW');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || mode !== 'DRAW') return;

    requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas || !canvas.parentElement) return;

      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = 160;

      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#000';

      ctxRef.current = ctx;
    });
  }, [open, mode]);

  const start = (e) => {
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    ctxRef.current.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctxRef.current.stroke();
  };

  const stop = () => setIsDrawing(false);

  const base64ToFile = (base64, filename) => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);

    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const save = async () => {
    let base64;

    if (mode === 'DRAW' && hasDrawn) {
      base64 = canvasRef.current.toDataURL('image/png');
    }

    if (mode === 'TYPE' && typedSignature.trim()) {
      const c = document.createElement('canvas');
      c.width = 600;
      c.height = 160;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.fillStyle = '#000';
      ctx.font = '48px Poppins';
      ctx.textAlign = 'center';
      ctx.fillText(typedSignature, c.width / 2, 100);
      base64 = c.toDataURL('image/png');
    }

    if (!base64) return;
    try {
      setIsSaving(true);
      const file = base64ToFile(base64, 'buyer-signature.png');

      const formData = new FormData();
      formData.append('userId', userDetail?.userId);
      formData.append('userType', 'User');
      formData.append('requestFile', file);

      const res = await postApiCall(UPLOAD_BUYER_SIGN, formData);

      if (res?.data?.status === 1) {
        sessionStorage.setItem(
          'buyerSignature',
          JSON.stringify({
            userId: userDetail?.userId,
            base64,
            filePath: res.data.filePath,
          })
        );
        onSave(base64);
        onApplied?.(base64);
        setIsSaving(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isApplyDisabled =
    (mode === 'DRAW' && !hasDrawn) ||
    (mode === 'TYPE' && !typedSignature.trim());

  const userName = useMemo(() => {
    const user = JSON.parse(localStorage.getItem('userDetail') || '{}');
    const name = `${user?.userName ?? ''} ${user?.userSurName ?? ''}`.trim();
    return name || 'User';
  }, []);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          ...poppins400,
          fontSize: matchessm ? '12px' : '18px',
          color: '#666666',
          lineHeight: '22px',
        }}
      >
        Edit Signature
      </DialogTitle>

      <DialogContent>
        <Box display="flex" gap={2} mb={2}>
          <Button
            fullWidth
            variant={mode === 'TYPE' ? 'contained' : 'outlined'}
            startIcon={<KeyboardIcon />}
            onClick={() => setMode('TYPE')}
            sx={{
              padding: matchessm ? '6px 18px' : '10px 39px',
              height: matchessm ? '35px' : '41px',
              fontSize: matchessm ? '12px' : '14px',
              color: mode === 'TYPE' ? '#fff' : '#666666',
              fontFamily: 'Poppins',
              border: '1px solid rgba(17, 35, 71, 0.1)',
              borderRadius: '5px',
              textTransform: 'none',
              background: mode === 'TYPE' && '#4CA4D6',
              '&:hover': {
                backgroundColor: mode === 'TYPE' ? '#4CA4D6' : 'transparent',
                color: mode === 'TYPE' ? '#fff' : '#666666',
                border: '1px solid rgba(17, 35, 71, 0.1)',
              },
            }}
          >
            Type
          </Button>
          <Button
            fullWidth
            variant={mode === 'DRAW' ? 'contained' : 'outlined'}
            startIcon={<EditIcon />}
            onClick={() => setMode('DRAW')}
            sx={{
              padding: matchessm ? '7px 17px' : '10px 39px',
              height: matchessm ? '35px' : '41px',
              fontSize: matchessm ? '12px' : '14px',
              color: mode === 'DRAW' ? '#fff' : '#666666',
              fontFamily: 'Poppins',
              background: mode === 'DRAW' && '#4CA4D6',
              borderRadius: '5px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: mode === 'DRAW' ? '#4CA4D6' : 'transparent',
                color: mode === 'DRAW' ? '#fff' : '#666666',
              },
              '&:disabled': {
                background: '#BFDDF0',
                color: '#FFFFFF',
              },
            }}
          >
            Draw
          </Button>
        </Box>

        <Box
          sx={{
            background: '#eef1f5',
            p: 2,
            borderRadius: 1,
            minHeight: 180,
          }}
        >
          {mode === 'DRAW' && (
            <canvas
              ref={canvasRef}
              style={{ width: '100%', cursor: 'crosshair' }}
              onMouseDown={start}
              onMouseMove={draw}
              onMouseUp={stop}
              onMouseLeave={stop}
            />
          )}

          {mode === 'TYPE' && (
            <>
              <TextField
                fullWidth
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Type your name"
              />
              <Box
                mt={2}
                textAlign="center"
                fontSize={32}
                fontFamily="Poppins"
                bgcolor="#fff"
                p={1}
              >
                {typedSignature || userName}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isSaving}
          sx={{
            padding: matchessm ? '6px 18px' : '10px 39px',
            height: matchessm ? '35px' : '41px',
            fontSize: matchessm ? '12px' : '14px',
            color: '#666666',
            fontFamily: 'Poppins',
            border: '1px solid rgba(17, 35, 71, 0.1)',
            borderRadius: '5px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              border: '1px solid rgba(17, 35, 71, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={isApplyDisabled || isSaving}
          sx={{
            padding: matchessm ? '7px 17px' : '10px 39px',
            height: matchessm ? '35px' : '41px',
            fontSize: matchessm ? '12px' : '14px',
            color: '#FFFFFF',
            fontFamily: 'Poppins',
            background: '#4CA4D6',
            borderRadius: '5px',
            textTransform: 'none',
            cursor: isApplyDisabled || isSaving ? 'not-allowed' : 'pointer',
            '&:hover': {
              backgroundColor: '#4CA4D6',
            },
            '&:disabled': {
              background: '#BFDDF0',
              color: '#FFFFFF',
              cursor: 'not-allowed',
              pointerEvents: 'auto',
            },
          }}
        >
          Apply Signature
        </Button>
        {isSaving && (
          <img
            src={Loading}
            alt="Loader"
            className="loaderImage"
            style={{
              height: '32px',
              width: '32px',
              marginLeft: '8px',
            }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomSignaturePadDialog;
